import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import styles from './Feedback.module.css';
import { MessageIcon, SendIcon, CheckIcon, AlertIcon } from '../../components/Icons';

const Feedback = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    type: 'suggestion', // suggestion, bug, feature, other
    title: '',
    content: '',
    contact: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // success, error, null

  // 反馈类型选项
  const feedbackTypes = [
    { value: 'suggestion', label: '建议', description: '功能改进建议' },
    { value: 'bug', label: '问题反馈', description: '发现的问题或错误' },
    { value: 'feature', label: '功能请求', description: '希望添加的新功能' },
    { value: 'book', label: '书籍请求', description: '希望添加的新书籍' },
    { value: 'other', label: '其他', description: '其他意见或想法' }
  ];

  // 处理表单输入
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 处理表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!formData.title.trim() || !formData.content.trim()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await api.post('/feedbacks/', {
        type: formData.type,
        title: formData.title.trim(),
        content: formData.content.trim(),
        contact: formData.contact.trim() || null,
      });

      setSubmitStatus('success');

      setTimeout(() => {
        navigate(-1);
      }, 3000);

    } catch (error) {
      console.error('提交反馈失败:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 重置表单
  const handleReset = () => {
    setFormData({
      type: 'suggestion',
      title: '',
      content: '',
      contact: ''
    });
    setSubmitStatus(null);
  };

  return (
    <div className={styles.feedback}>
      {/* 页面头部 */}
      <div className={styles.header}>
        <button 
          type="button"
          className={styles.backBtn}
          onClick={() => navigate(-1)}
          aria-label="返回"
        >
          <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
        </button>
      </div>

      {/* 反馈表单 */}
      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.feedbackForm}>
          {/* 反馈类型选择 */}
          <div className={styles.formGroup}>
            <label className={styles.label}>反馈类型 *</label>
            <div className={styles.typeSelector}>
              {feedbackTypes.map(type => (
                <label key={type.value} className={styles.typeOption}>
                  <input
                    type="radio"
                    name="type"
                    value={type.value}
                    checked={formData.type === type.value}
                    onChange={handleInputChange}
                    className={styles.radioInput}
                  />
                  <div className={styles.typeCard}>
                    <div className={styles.typeLabel}>{type.label}</div>
                    {/* <div className={styles.typeDescription}>{type.description}</div> */}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* 标题输入 */}
          <div className={styles.formGroup}>
            <label htmlFor="title" className={styles.label}>
              标题 *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="请简要描述您的反馈"
              className={styles.textInput}
              maxLength={100}
              required
            />
            <div className={styles.charCount}>
              {formData.title.length}/100
            </div>
          </div>

          {/* 详细内容 */}
          <div className={styles.formGroup}>
            <label htmlFor="content" className={styles.label}>
              详细描述 *
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="请详细描述您的反馈内容，包括具体的问题、建议或想法..."
              className={styles.textarea}
              rows={6}
              maxLength={1000}
              required
            />
            <div className={styles.charCount}>
              {formData.content.length}/1000
            </div>
          </div>

          {/* 联系方式 */}
          <div className={styles.formGroup}>
            <label htmlFor="contact" className={styles.label}>
              联系方式（可选）
            </label>
            <input
              type="text"
              id="contact"
              name="contact"
              value={formData.contact}
              onChange={handleInputChange}
              placeholder="邮箱、微信或其他联系方式（便于我们回复）"
              className={styles.textInput}
              maxLength={50}
            />
         
          </div>

          {/* 提交状态提示 */}
          {submitStatus === 'success' && (
            <div className={styles.statusMessage + ' ' + styles.success}>
              <CheckIcon size={20} />
              反馈提交成功！感谢您的宝贵意见，我们会认真考虑您的建议。
            </div>
          )}

          {submitStatus === 'error' && (
            <div className={styles.statusMessage + ' ' + styles.error}>
              <AlertIcon size={20} />
              提交失败，请检查必填字段后重试。
            </div>
          )}

          {/* 操作按钮 */}
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={handleReset}
              className={styles.resetBtn}
              disabled={isSubmitting}
            >
              重置
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isSubmitting || !formData.title.trim() || !formData.content.trim()}
            >
              {isSubmitting ? (
                <>
                  <div className={styles.spinner}></div>
                  提交中...
                </>
              ) : (
                <>
                  <SendIcon size={20} />
                  提交反馈
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* 反馈说明 */}
      <div className={styles.feedbackInfo}>
        <h3>反馈说明</h3>
        <ul>
          <li>我们重视每一位用户的反馈，会认真阅读每一条建议</li>
          <li>对于问题反馈，我们会尽快修复并通知您</li>
          <li>功能建议会根据优先级和可行性进行考虑</li>
          <li>您的个人信息将被严格保护，仅用于回复反馈</li>
        </ul>
      </div>
    </div>
  );
};

export default Feedback;
